import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Building2, Gem, ChevronRight, ChevronLeft, CheckCircle, 
  FileText, AlertCircle, Info
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = [
  'Energy / Oil & Gas', 'Minerals', 'Other Industrial'
];

const COUNTRIES = [
  'Algeria', 'Angola', 'Bahrain', 'Benin', 'Botswana', 'Burkina Faso', 'Burundi',
  'Cameroon', 'Cape Verde', 'Central African Republic', 'Chad', 'Comoros',
  'Cote d\'Ivoire', 'Djibouti', 'DRC', 'Egypt', 'Equatorial Guinea', 'Eritrea',
  'Eswatini', 'Ethiopia', 'Gabon', 'Gambia', 'Ghana', 'Guinea', 'Guinea-Bissau',
  'Iraq', 'Iran', 'Israel', 'Jordan', 'Kenya', 'Kuwait', 'Lebanon', 'Lesotho',
  'Liberia', 'Libya', 'Madagascar', 'Malawi', 'Mali', 'Mauritania', 'Mauritius',
  'Morocco', 'Mozambique', 'Namibia', 'Niger', 'Nigeria', 'Oman', 'Palestine',
  'Qatar', 'Republic of Congo', 'Rwanda', 'Saudi Arabia', 'Senegal',
  'Seychelles', 'Sierra Leone', 'Somalia', 'South Africa', 'South Sudan',
  'Sudan', 'Syria', 'Tanzania', 'Togo', 'Tunisia', 'Turkey', 'UAE',
  'Uganda', 'Yemen', 'Zambia', 'Zimbabwe'
];

const COMMODITIES = [
  'Aluminum', 'Antimony', 'Bauxite', 'Beryllium', 'Bismuth',
  'Cadmium', 'Chromium', 'Coal', 'Cobalt', 'Coltan',
  'Copper', 'Crude Oil', 'Diamond', 'Fluorspar',
  'Gallium', 'Germanium', 'Gold', 'Graphite',
  'Hafnium', 'Ilmenite', 'Indium', 'Iridium', 'Iron Ore',
  'Lead', 'Lithium', 'Magnesium', 'Manganese', 'Mercury',
  'Molybdenum', 'Natural Gas', 'Nickel', 'Niobium',
  'Palladium', 'Phosphate', 'Platinum', 'Potash',
  'Rare Earth Elements', 'Rhenium', 'Rhodium', 'Ruthenium', 'Rutile',
  'Scandium', 'Selenium', 'Silicon', 'Silver',
  'Tantalum', 'Tellurium', 'Tin', 'Titanium', 'Tungsten',
  'Uranium', 'Vanadium', 'Zinc', 'Zirconium'
];

const INFRASTRUCTURE_DOCUMENTS = [
  'Feasibility Study',
  'Technical Specifications',
  'Environmental Impact Assessment',
  'Land Title / Lease Agreement',
  'Grid Connection Agreement',
  'Power Purchase Agreement (PPA)',
  'Engineering Designs',
  'Permits and Licenses',
  'Financial Model',
  'Project Timeline',
  'EPC Contractor Agreement',
  'Insurance Documentation',
  'Regulatory Approvals',
  'Sponsor Track Record'
];

const MINERALS_DOCUMENTS = [
  'Geological Survey Report',
  'Mining License',
  'Resource Assessment',
  'Offtake Agreement',
  'Quality Certification',
  'Export License',
  'Third-party Inspection Reports',
  'Sample Analysis Results',
  'Supply Chain Documentation',
  'Pricing Methodology',
  'Contract Template',
  'Insurance Coverage',
  'Seller Track Record',
  'Compliance Certificates'
];

export function SubmitDealPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeaders } = useAuth();
  const initialType = location.state?.dealType || null;
  const [step, setStep] = useState(initialType ? 2 : 1);
  const [dealType, setDealType] = useState(initialType || '');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Infrastructure Deal State
  const [infraDeal, setInfraDeal] = useState({
    name: '',
    sector: '',
    country: '',
    developer: user?.organization || '',
    capital_required: '',
    equity_split: '',
    debt_split: '',
    target_irr: '',
    stage: 'concept',
    permits_status: '',
    epc_readiness: 'not_started',
    offtake_readiness: 'not_started',
    summary: '',
    risk_flags: [],
    confidentiality_level: 'restricted',
    contract_status: 'early_discussion',
    spv_status: 'not_started',
    existing_partners: [],
    project_capacity: '',
    project_capacity_unit: 'MW',
    construction_period: '',
    operational_life: '',
    debt_tenor: '',
    debt_rate: '',
    lcoe: '',
    offtaker: '',
    ppa_term: '',
    ppa_price: '',
    land_status: '',
    grid_connection_status: '',
    environmental_status: '',
    jobs_created: '',
    co2_impact: '',
    financial_close_target: '',
    cod_target: '',
    sponsors: '',
    financial_advisor: '',
    legal_advisor: '',
    technical_advisor: ''
  });

  // Minerals Deal State
  const [mineralDeal, setMineralDeal] = useState({
    commodity: '',
    grade: '',
    volume: '',
    volume_unit: 'MT/year',
    origin_country: '',
    delivery_basis: '',
    pricing_basis: '',
    contract_duration: '',
    financing_need: false,
    financing_amount: '',
    certification: [],
    target_buyers: '',
    description: '',
    // Additional institutional fields
    mine_location: '',
    production_capacity: '',
    reserves_estimate: '',
    resource_category: 'Measured',
    extraction_method: '',
    processing_method: '',
    logistics_route: '',
    port_of_export: '',
    quality_specs: '',
    impurities: '',
    moisture_content: '',
    packaging: '',
    min_order_quantity: '',
    payment_terms: '',
    delivery_timeframe: '',
    inspection_procedures: '',
    penalties_incentives: '',
    supplier_history: '',
    annual_production: '',
    scalability: ''
  });

  const [availableDocs, setAvailableDocs] = useState([]);
  const [riskFlags, setRiskFlags] = useState([]);

  const handleInfraChange = (field, value) => {
    setInfraDeal(prev => ({ ...prev, [field]: value }));
  };

  const handleMineralChange = (field, value) => {
    setMineralDeal(prev => ({ ...prev, [field]: value }));
  };

  const handleDocToggle = (doc) => {
    setAvailableDocs(prev => 
      prev.includes(doc) ? prev.filter(d => d !== doc) : [...prev, doc]
    );
  };

  const handleRiskToggle = (risk) => {
    setRiskFlags(prev => 
      prev.includes(risk) ? prev.filter(r => r !== risk) : [...prev, risk]
    );
  };

  const totalSteps = dealType === 'minerals' ? 5 : 6;

  const validateStep = () => {
    const errs = {};
    if (step === 1) return dealType !== '';
    if (dealType === 'infrastructure') {
      if (step === 2) {
        if (!infraDeal.name) errs.name = 'Project name is required';
        if (!infraDeal.sector) errs.sector = 'Please select a valid sector';
        if (!infraDeal.country) errs.country = 'Country is required';
        if (!infraDeal.developer) errs.developer = 'Developer name is required';
        if (!infraDeal.capital_required) errs.capital_required = 'Capital required is missing';
        if (!infraDeal.summary) errs.summary = 'Summary is required';
      }
      if (step === 3) {
        if (!infraDeal.equity_split) errs.equity_split = 'Equity split is required';
        if (!infraDeal.debt_split) errs.debt_split = 'Debt split is required';
        if (!infraDeal.stage) errs.stage = 'Stage is required';
      }
    } else {
      if (step === 2) {
        if (!mineralDeal.commodity) errs.commodity = 'Commodity is required';
        if (!mineralDeal.grade) errs.grade = 'Grade specification is required';
        if (!mineralDeal.volume) errs.volume = 'Volume is required';
        if (!mineralDeal.origin_country) errs.origin_country = 'Origin country is required';
        if (!mineralDeal.description) errs.description = 'Description is required';
      }
      if (step === 3) {
        if (!mineralDeal.pricing_basis) errs.pricing_basis = 'Pricing basis is required';
        if (!mineralDeal.delivery_basis) errs.delivery_basis = 'Delivery basis is required';
        if (!mineralDeal.contract_duration) errs.contract_duration = 'Contract duration is required';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const fieldErr = (field) => errors[field] ? 'border-red-500 focus-visible:ring-red-500' : '';

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (dealType === 'infrastructure') {
        const dealData = {
          ...infraDeal,
          capital_required: parseFloat(infraDeal.capital_required),
          equity_split: parseFloat(infraDeal.equity_split),
          debt_split: parseFloat(infraDeal.debt_split),
          target_irr: infraDeal.target_irr ? parseFloat(infraDeal.target_irr) : null,
          risk_flags: riskFlags,
          status: 'pending_approval'
        };
        await axios.post(`${API}/deals`, dealData, { headers: getAuthHeaders() });
        toast.success('Deal submitted for review');
        navigate('/deals');
      } else {
        const mineralData = {
          ...mineralDeal,
          volume: parseFloat(mineralDeal.volume),
          financing_amount: mineralDeal.financing_amount ? parseFloat(mineralDeal.financing_amount) : null,
          status: 'pending_approval'
        };
        await axios.post(`${API}/minerals`, mineralData, { headers: getAuthHeaders() });
        toast.success('Deal submitted for review');
        navigate('/minerals');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  const renderStepIndicator = () => {
    const steps = dealType === 'minerals' 
      ? ['Commodity Overview', 'Commercial Terms', 'Documents', 'Review']
      : dealType === 'infrastructure'
      ? ['Project Overview', 'Financial Structure', 'Status & Terms', 'Documents', 'Review']
      : ['Select Type'];
    
    const startStep = initialType ? 2 : 1;
    const displaySteps = initialType ? steps : ['Select Type', ...steps];
    
    return (
      <div className="flex items-center justify-center space-x-2 mb-8">
        {displaySteps.map((label, idx) => {
          const stepNum = idx + 1;
          return (
            <div key={stepNum} className="flex items-center">
              <div
                className={cn(
                  'w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold',
                  step >= stepNum ? 'bg-accent text-white' : 'bg-muted text-muted-foreground'
                )}
                title={label}
              >
                {step > stepNum ? <CheckCircle size={20} /> : stepNum}
              </div>
              {idx < displaySteps.length - 1 && (
                <div
                  className={cn(
                    'w-12 h-0.5',
                    step > stepNum ? 'bg-accent' : 'bg-muted'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold tracking-tight">
            {dealType === 'infrastructure' ? 'Submit Infrastructure Deal' : 
             dealType === 'minerals' ? 'Submit Mineral Listing' : 'Submit Deal'}
          </h1>
          <p className="text-muted-foreground mt-1">
            Provide comprehensive details for institutional investor evaluation
          </p>
        </div>

        {renderStepIndicator()}

        {/* Step 1: Deal Type Selection */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle>What type of opportunity are you submitting?</CardTitle>
              <CardDescription>
                Select the category that best describes your deal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className={cn(
                  'p-6 border-2 rounded-sm cursor-pointer transition-all',
                  dealType === 'infrastructure' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
                )}
                onClick={() => setDealType('infrastructure')}
                data-testid="select-infrastructure"
              >
                <div className="flex items-start space-x-4">
                  <Building2 size={32} className="text-accent mt-1" />
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">Infrastructure Project</h3>
                    <p className="text-sm text-muted-foreground">
                      Energy, water, transport, or industrial infrastructure requiring equity and debt financing.
                      Includes renewable energy projects, PPPs, ports, roads, water treatment, and industrial facilities.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Badge variant="outline">Renewable Energy</Badge>
                      <Badge variant="outline">Transport</Badge>
                      <Badge variant="outline">Water</Badge>
                      <Badge variant="outline">Industrial</Badge>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className={cn(
                  'p-6 border-2 rounded-sm cursor-pointer transition-all',
                  dealType === 'minerals' ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50'
                )}
                onClick={() => setDealType('minerals')}
                data-testid="select-minerals"
              >
                <div className="flex items-start space-x-4">
                  <Gem size={32} className="text-accent mt-1" />
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-2">Mineral / Commodity Offtake</h3>
                    <p className="text-sm text-muted-foreground">
                      Natural minerals, battery materials, or industrial commodities available for offtake agreements.
                      Includes copper, lithium, cobalt, graphite, and other strategic minerals.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Badge variant="outline">Battery Minerals</Badge>
                      <Badge variant="outline">Base Metals</Badge>
                      <Badge variant="outline">Industrial</Badge>
                      <Badge variant="outline">Precious Metals</Badge>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Infrastructure Steps */}
        {dealType === 'infrastructure' && step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Project Overview</CardTitle>
              <CardDescription>Core project information and summary</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Project Name *</Label>
                  <Input
                    id="name"
                    value={infraDeal.name}
                    onChange={(e) => handleInfraChange('name', e.target.value)}
                    placeholder="e.g., 100 MW Solar PV Project - Northern Ghana"
                  />
                </div>

                <div>
                  <Label htmlFor="sector">Sector *</Label>
                  <Select value={infraDeal.sector} onValueChange={(v) => handleInfraChange('sector', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select sector" />
                    </SelectTrigger>
                    <SelectContent>
                      {SECTORS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="country">Country *</Label>
                  <Select value={infraDeal.country} onValueChange={(v) => handleInfraChange('country', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="developer">Sponsor / Developer *</Label>
                  <Input
                    id="developer"
                    value={infraDeal.developer}
                    onChange={(e) => handleInfraChange('developer', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="capital_required">Total Capital Required (USD) *</Label>
                  <Input
                    id="capital_required"
                    type="number"
                    value={infraDeal.capital_required}
                    onChange={(e) => handleInfraChange('capital_required', e.target.value)}
                    placeholder="e.g., 85000000"
                  />
                </div>

                <div>
                  <Label htmlFor="project_capacity">Project Capacity</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="project_capacity"
                      value={infraDeal.project_capacity}
                      onChange={(e) => handleInfraChange('project_capacity', e.target.value)}
                      placeholder="100"
                    />
                    <Select value={infraDeal.project_capacity_unit} onValueChange={(v) => handleInfraChange('project_capacity_unit', v)}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="MW">MW</SelectItem>
                        <SelectItem value="GW">GW</SelectItem>
                        <SelectItem value="MIGD">MIGD</SelectItem>
                        <SelectItem value="MT">MT</SelectItem>
                        <SelectItem value="km">km</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="construction_period">Construction Period (months)</Label>
                  <Input
                    id="construction_period"
                    type="number"
                    value={infraDeal.construction_period}
                    onChange={(e) => handleInfraChange('construction_period', e.target.value)}
                    placeholder="18"
                  />
                </div>

                <div>
                  <Label htmlFor="operational_life">Operational Life (years)</Label>
                  <Input
                    id="operational_life"
                    type="number"
                    value={infraDeal.operational_life}
                    onChange={(e) => handleInfraChange('operational_life', e.target.value)}
                    placeholder="25"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="summary">Project Summary *</Label>
                  <Textarea
                    id="summary"
                    value={infraDeal.summary}
                    onChange={(e) => handleInfraChange('summary', e.target.value)}
                    rows={4}
                    placeholder="Comprehensive project description including technology, location, commercial structure, and key value propositions..."
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {dealType === 'infrastructure' && step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Financial Structure</CardTitle>
              <CardDescription>Capital structure and financial returns</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="equity_split">Equity (%) *</Label>
                  <Input
                    id="equity_split"
                    type="number"
                    value={infraDeal.equity_split}
                    onChange={(e) => handleInfraChange('equity_split', e.target.value)}
                    placeholder="30"
                  />
                </div>

                <div>
                  <Label htmlFor="debt_split">Debt (%) *</Label>
                  <Input
                    id="debt_split"
                    type="number"
                    value={infraDeal.debt_split}
                    onChange={(e) => handleInfraChange('debt_split', e.target.value)}
                    placeholder="70"
                  />
                </div>

                <div>
                  <Label htmlFor="target_irr">Target IRR (%)</Label>
                  <Input
                    id="target_irr"
                    type="number"
                    step="0.1"
                    value={infraDeal.target_irr}
                    onChange={(e) => handleInfraChange('target_irr', e.target.value)}
                    placeholder="14.5"
                  />
                </div>

                <div>
                  <Label htmlFor="debt_tenor">Debt Tenor (years)</Label>
                  <Input
                    id="debt_tenor"
                    type="number"
                    value={infraDeal.debt_tenor}
                    onChange={(e) => handleInfraChange('debt_tenor', e.target.value)}
                    placeholder="15"
                  />
                </div>

                <div>
                  <Label htmlFor="debt_rate">Debt Interest Rate (%)</Label>
                  <Input
                    id="debt_rate"
                    type="number"
                    step="0.1"
                    value={infraDeal.debt_rate}
                    onChange={(e) => handleInfraChange('debt_rate', e.target.value)}
                    placeholder="8.5"
                  />
                </div>

                <div>
                  <Label htmlFor="lcoe">LCOE (USD/MWh)</Label>
                  <Input
                    id="lcoe"
                    type="number"
                    step="0.01"
                    value={infraDeal.lcoe}
                    onChange={(e) => handleInfraChange('lcoe', e.target.value)}
                    placeholder="0.045"
                  />
                </div>

                <div>
                  <Label htmlFor="stage">Project Stage *</Label>
                  <Select value={infraDeal.stage} onValueChange={(v) => handleInfraChange('stage', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="concept">Concept</SelectItem>
                      <SelectItem value="feasibility">Feasibility</SelectItem>
                      <SelectItem value="development">Development</SelectItem>
                      <SelectItem value="financial_close">Financial Close</SelectItem>
                      <SelectItem value="construction">Construction</SelectItem>
                      <SelectItem value="operational">Operational</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="financial_close_target">Financial Close Target</Label>
                  <Input
                    id="financial_close_target"
                    value={infraDeal.financial_close_target}
                    onChange={(e) => handleInfraChange('financial_close_target', e.target.value)}
                    placeholder="2026-Q4"
                  />
                </div>

                <div>
                  <Label htmlFor="cod_target">COD Target</Label>
                  <Input
                    id="cod_target"
                    value={infraDeal.cod_target}
                    onChange={(e) => handleInfraChange('cod_target', e.target.value)}
                    placeholder="2026-Q3"
                  />
                </div>

                <div>
                  <Label htmlFor="sponsors">Sponsors / Co-Sponsors</Label>
                  <Input
                    id="sponsors"
                    value={infraDeal.sponsors}
                    onChange={(e) => handleInfraChange('sponsors', e.target.value)}
                    placeholder="Comma-separated list"
                  />
                </div>

                <div>
                  <Label htmlFor="financial_advisor">Financial Advisor</Label>
                  <Input
                    id="financial_advisor"
                    value={infraDeal.financial_advisor}
                    onChange={(e) => handleInfraChange('financial_advisor', e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="legal_advisor">Legal Advisor</Label>
                  <Input
                    id="legal_advisor"
                    value={infraDeal.legal_advisor}
                    onChange={(e) => handleInfraChange('legal_advisor', e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {dealType === 'infrastructure' && step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle>Project Status & Commercial Terms</CardTitle>
              <CardDescription>Permits, offtake, and project readiness</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="permits_status">Permits Status</Label>
                  <Textarea
                    id="permits_status"
                    value={infraDeal.permits_status}
                    onChange={(e) => handleInfraChange('permits_status', e.target.value)}
                    rows={2}
                    placeholder="Detail current status of all permits, licenses, and regulatory approvals..."
                  />
                </div>

                <div>
                  <Label htmlFor="epc_readiness">EPC Readiness</Label>
                  <Select value={infraDeal.epc_readiness} onValueChange={(v) => handleInfraChange('epc_readiness', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="ready">Ready</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="offtake_readiness">Offtake Readiness</Label>
                  <Select value={infraDeal.offtake_readiness} onValueChange={(v) => handleInfraChange('offtake_readiness', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="secured">Secured</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="offtaker">Offtaker / PPA Counterparty</Label>
                  <Input
                    id="offtaker"
                    value={infraDeal.offtaker}
                    onChange={(e) => handleInfraChange('offtaker', e.target.value)}
                    placeholder="e.g., Electricity Company of Ghana (ECG)"
                  />
                </div>

                <div>
                  <Label htmlFor="ppa_term">PPA Term (years)</Label>
                  <Input
                    id="ppa_term"
                    type="number"
                    value={infraDeal.ppa_term}
                    onChange={(e) => handleInfraChange('ppa_term', e.target.value)}
                    placeholder="20"
                  />
                </div>

                <div>
                  <Label htmlFor="ppa_price">PPA Price (USD/MWh)</Label>
                  <Input
                    id="ppa_price"
                    type="number"
                    step="0.001"
                    value={infraDeal.ppa_price}
                    onChange={(e) => handleInfraChange('ppa_price', e.target.value)}
                    placeholder="0.065"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="land_status">Land Status</Label>
                  <Input
                    id="land_status"
                    value={infraDeal.land_status}
                    onChange={(e) => handleInfraChange('land_status', e.target.value)}
                    placeholder="e.g., Secured - 250 hectares, title deed obtained"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="grid_connection_status">Grid Connection Status</Label>
                  <Input
                    id="grid_connection_status"
                    value={infraDeal.grid_connection_status}
                    onChange={(e) => handleInfraChange('grid_connection_status', e.target.value)}
                    placeholder="e.g., NERTDC approval pending"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="environmental_status">Environmental Assessment Status</Label>
                  <Input
                    id="environmental_status"
                    value={infraDeal.environmental_status}
                    onChange={(e) => handleInfraChange('environmental_status', e.target.value)}
                    placeholder="e.g., Completed - Category B, approved by EPA"
                  />
                </div>

                <div>
                  <Label htmlFor="jobs_created">Jobs Created (estimate)</Label>
                  <Input
                    id="jobs_created"
                    type="number"
                    value={infraDeal.jobs_created}
                    onChange={(e) => handleInfraChange('jobs_created', e.target.value)}
                    placeholder="450"
                  />
                </div>

                <div>
                  <Label htmlFor="co2_impact">CO2 Avoided (MT/year)</Label>
                  <Input
                    id="co2_impact"
                    type="number"
                    value={infraDeal.co2_impact}
                    onChange={(e) => handleInfraChange('co2_impact', e.target.value)}
                    placeholder="85000"
                  />
                </div>

                <div>
                  <Label htmlFor="confidentiality_level">Confidentiality Level</Label>
                  <Select value={infraDeal.confidentiality_level} onValueChange={(v) => handleInfraChange('confidentiality_level', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Public</SelectItem>
                      <SelectItem value="restricted">Restricted</SelectItem>
                      <SelectItem value="confidential">Confidential</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Contract Status</Label>
                  <Select value={infraDeal.contract_status} onValueChange={(v) => handleInfraChange('contract_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="early_discussion">Early Discussion</SelectItem>
                      <SelectItem value="in_negotiation">In Negotiation</SelectItem>
                      <SelectItem value="awarded">Awarded</SelectItem>
                      <SelectItem value="contract_signed">Contract Signed</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>SPV Status</Label>
                  <Select value={infraDeal.spv_status} onValueChange={(v) => handleInfraChange('spv_status', v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="not_started">Not Started</SelectItem>
                      <SelectItem value="in_formation">In Formation</SelectItem>
                      <SelectItem value="formed">Formed</SelectItem>
                      <SelectItem value="pending_information">Pending Information</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label>Existing Partners Engaged</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {['Investor', 'EPC', 'Offtaker', 'Advisor', 'Lender'].map(partner => (
                      <Button
                        key={partner}
                        type="button"
                        size="sm"
                        variant={infraDeal.existing_partners.includes(partner.toLowerCase()) ? 'default' : 'outline'}
                        className="text-xs"
                        onClick={() => {
                          const val = partner.toLowerCase();
                          const updated = infraDeal.existing_partners.includes(val)
                            ? infraDeal.existing_partners.filter(p => p !== val)
                            : [...infraDeal.existing_partners, val];
                          handleInfraChange('existing_partners', updated);
                        }}
                      >{partner}</Button>
                    ))}
                  </div>
                </div>
              </div>

              <Separator className="my-6" />

              <div>
                <Label className="mb-3 block">Risk Flags (select all that apply)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {['Grid connection timeline', 'Currency risk', 'Political risk', 'Offtaker credit risk', 
                    'Construction risk', 'Technology performance', 'Commodity price volatility', 
                    'Regulatory risk', 'Land acquisition'].map(risk => (
                    <div key={risk} className="flex items-center space-x-2">
                      <Checkbox
                        checked={riskFlags.includes(risk)}
                        onCheckedChange={() => handleRiskToggle(risk)}
                      />
                      <span className="text-sm">{risk}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Minerals Steps */}
        {dealType === 'minerals' && step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle>Commodity Overview</CardTitle>
              <CardDescription>Core mineral specifications and origin</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="commodity">Commodity *</Label>
                  <Select value={mineralDeal.commodity} onValueChange={(v) => handleMineralChange('commodity', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select commodity" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMMODITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="origin_country">Origin Country *</Label>
                  <Select value={mineralDeal.origin_country} onValueChange={(v) => handleMineralChange('origin_country', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="grade">Grade / Specification *</Label>
                  <Input
                    id="grade"
                    value={mineralDeal.grade}
                    onChange={(e) => handleMineralChange('grade', e.target.value)}
                    placeholder="e.g., 99.99% purity (Grade A) or 6.0% Li2O"
                  />
                </div>

                <div>
                  <Label htmlFor="volume">Annual Volume *</Label>
                  <Input
                    id="volume"
                    type="number"
                    value={mineralDeal.volume}
                    onChange={(e) => handleMineralChange('volume', e.target.value)}
                    placeholder="15000"
                  />
                </div>

                <div>
                  <Label htmlFor="volume_unit">Volume Unit</Label>
                  <Select value={mineralDeal.volume_unit} onValueChange={(v) => handleMineralChange('volume_unit', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MT/year">MT/year</SelectItem>
                      <SelectItem value="DMT/year">DMT/year</SelectItem>
                      <SelectItem value="kg/year">kg/year</SelectItem>
                      <SelectItem value="oz/year">oz/year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="col-span-2">
                  <Label htmlFor="mine_location">Mine / Production Location</Label>
                  <Input
                    id="mine_location"
                    value={mineralDeal.mine_location}
                    onChange={(e) => handleMineralChange('mine_location', e.target.value)}
                    placeholder="Specific location or region"
                  />
                </div>

                <div>
                  <Label htmlFor="production_capacity">Current Production Capacity</Label>
                  <Input
                    id="production_capacity"
                    value={mineralDeal.production_capacity}
                    onChange={(e) => handleMineralChange('production_capacity', e.target.value)}
                    placeholder="Annual capacity"
                  />
                </div>

                <div>
                  <Label htmlFor="reserves_estimate">Reserves Estimate</Label>
                  <Input
                    id="reserves_estimate"
                    value={mineralDeal.reserves_estimate}
                    onChange={(e) => handleMineralChange('reserves_estimate', e.target.value)}
                    placeholder="Total proven/probable reserves"
                  />
                </div>

                <div>
                  <Label htmlFor="resource_category">Resource Category</Label>
                  <Select value={mineralDeal.resource_category} onValueChange={(v) => handleMineralChange('resource_category', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Measured">Measured</SelectItem>
                      <SelectItem value="Indicated">Indicated</SelectItem>
                      <SelectItem value="Inferred">Inferred</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="extraction_method">Extraction Method</Label>
                  <Input
                    id="extraction_method"
                    value={mineralDeal.extraction_method}
                    onChange={(e) => handleMineralChange('extraction_method', e.target.value)}
                    placeholder="e.g., Open pit, Underground, etc."
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="description">Commodity Description *</Label>
                  <Textarea
                    id="description"
                    value={mineralDeal.description}
                    onChange={(e) => handleMineralChange('description', e.target.value)}
                    rows={4}
                    placeholder="Detailed description including quality, consistency, supply chain, track record..."
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="target_buyers">Target Buyers</Label>
                  <Textarea
                    id="target_buyers"
                    value={mineralDeal.target_buyers}
                    onChange={(e) => handleMineralChange('target_buyers', e.target.value)}
                    rows={2}
                    placeholder="Industries and buyer types"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {dealType === 'minerals' && step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle>Commercial Terms</CardTitle>
              <CardDescription>Pricing, delivery, and contract structure</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="pricing_basis">Pricing Basis *</Label>
                  <Input
                    id="pricing_basis"
                    value={mineralDeal.pricing_basis}
                    onChange={(e) => handleMineralChange('pricing_basis', e.target.value)}
                    placeholder="e.g., LME minus 5%, Platts assessment linked, Fixed price"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="delivery_basis">Delivery Basis *</Label>
                  <Input
                    id="delivery_basis"
                    value={mineralDeal.delivery_basis}
                    onChange={(e) => handleMineralChange('delivery_basis', e.target.value)}
                    placeholder="e.g., FOB Dar es Salaam, CIF China, Ex-Works"
                  />
                </div>

                <div>
                  <Label htmlFor="contract_duration">Contract Duration *</Label>
                  <Input
                    id="contract_duration"
                    value={mineralDeal.contract_duration}
                    onChange={(e) => handleMineralChange('contract_duration', e.target.value)}
                    placeholder="e.g., 3 years, 5 years"
                  />
                </div>

                <div>
                  <Label htmlFor="min_order_quantity">Minimum Order Quantity</Label>
                  <Input
                    id="min_order_quantity"
                    value={mineralDeal.min_order_quantity}
                    onChange={(e) => handleMineralChange('min_order_quantity', e.target.value)}
                    placeholder="MT per shipment"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="payment_terms">Payment Terms</Label>
                  <Input
                    id="payment_terms"
                    value={mineralDeal.payment_terms}
                    onChange={(e) => handleMineralChange('payment_terms', e.target.value)}
                    placeholder="e.g., LC at sight, 30 days net"
                  />
                </div>

                <div>
                  <Label htmlFor="delivery_timeframe">Delivery Timeframe</Label>
                  <Input
                    id="delivery_timeframe"
                    value={mineralDeal.delivery_timeframe}
                    onChange={(e) => handleMineralChange('delivery_timeframe', e.target.value)}
                    placeholder="e.g., 45 days from order"
                  />
                </div>

                <div>
                  <Label htmlFor="port_of_export">Port of Export</Label>
                  <Input
                    id="port_of_export"
                    value={mineralDeal.port_of_export}
                    onChange={(e) => handleMineralChange('port_of_export', e.target.value)}
                    placeholder="Primary export port"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="logistics_route">Logistics Route</Label>
                  <Input
                    id="logistics_route"
                    value={mineralDeal.logistics_route}
                    onChange={(e) => handleMineralChange('logistics_route', e.target.value)}
                    placeholder="Mine to port transportation details"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="quality_specs">Quality Specifications</Label>
                  <Textarea
                    id="quality_specs"
                    value={mineralDeal.quality_specs}
                    onChange={(e) => handleMineralChange('quality_specs', e.target.value)}
                    rows={3}
                    placeholder="Detailed quality parameters, testing methods, and acceptance criteria"
                  />
                </div>

                <div>
                  <Label htmlFor="impurities">Impurities / Contaminants</Label>
                  <Input
                    id="impurities"
                    value={mineralDeal.impurities}
                    onChange={(e) => handleMineralChange('impurities', e.target.value)}
                    placeholder="Maximum allowable levels"
                  />
                </div>

                <div>
                  <Label htmlFor="moisture_content">Moisture Content</Label>
                  <Input
                    id="moisture_content"
                    value={mineralDeal.moisture_content}
                    onChange={(e) => handleMineralChange('moisture_content', e.target.value)}
                    placeholder="e.g., < 8%"
                  />
                </div>

                <div className="col-span-2">
                  <Label htmlFor="inspection_procedures">Inspection & Verification Procedures</Label>
                  <Textarea
                    id="inspection_procedures"
                    value={mineralDeal.inspection_procedures}
                    onChange={(e) => handleMineralChange('inspection_procedures', e.target.value)}
                    rows={2}
                    placeholder="Third-party inspection, sampling protocols, dispute resolution"
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={mineralDeal.financing_need}
                    onCheckedChange={(checked) => handleMineralChange('financing_need', checked)}
                  />
                  <Label>Financing Required</Label>
                </div>

                {mineralDeal.financing_need && (
                  <div>
                    <Label htmlFor="financing_amount">Financing Amount (USD)</Label>
                    <Input
                      id="financing_amount"
                      type="number"
                      value={mineralDeal.financing_amount}
                      onChange={(e) => handleMineralChange('financing_amount', e.target.value)}
                      placeholder="e.g., 8500000"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Document Checklist - Step 4 for both types */}
        {step === (dealType === 'minerals' ? 4 : 5) && (
          <Card>
            <CardHeader>
              <CardTitle>Document Availability Checklist</CardTitle>
              <CardDescription>
                Indicate which documents are available for investor due diligence
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-sm p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <Info className="text-blue-600 dark:text-blue-400 mt-0.5" size={20} />
                  <div className="text-sm text-blue-900 dark:text-blue-100">
                    <p className="font-semibold mb-1">Document Requirement</p>
                    <p>Investors will request these documents during due diligence. Select all currently available documents. Missing critical documents may delay or prevent deal evaluation.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(dealType === 'infrastructure' ? INFRASTRUCTURE_DOCUMENTS : MINERALS_DOCUMENTS).map(doc => (
                  <div
                    key={doc}
                    className={cn(
                      'flex items-center space-x-3 p-3 border rounded-sm cursor-pointer transition-colors',
                      availableDocs.includes(doc) ? 'border-accent bg-accent/5' : 'border-border hover:bg-muted'
                    )}
                    onClick={() => handleDocToggle(doc)}
                  >
                    <Checkbox checked={availableDocs.includes(doc)} />
                    <div className="flex-1 flex items-center justify-between">
                      <span className="text-sm font-medium">{doc}</span>
                      {availableDocs.includes(doc) && <CheckCircle size={16} className="text-accent" />}
                    </div>
                  </div>
                ))}
              </div>

              {availableDocs.length < 5 && (
                <div className="mt-6 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-sm p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="text-yellow-600 dark:text-yellow-400 mt-0.5" size={20} />
                    <div className="text-sm text-yellow-900 dark:text-yellow-100">
                      <p className="font-semibold mb-1">Limited Documentation</p>
                      <p>You have selected fewer than 5 documents. This may significantly impact investor interest. Consider preparing additional documentation before submission.</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6">
                <p className="text-sm text-muted-foreground">
                  <strong>Documents Available:</strong> {availableDocs.length} of {dealType === 'infrastructure' ? INFRASTRUCTURE_DOCUMENTS.length : MINERALS_DOCUMENTS.length}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review & Submit - Final Step */}
        {step === totalSteps && (
          <Card>
            <CardHeader>
              <CardTitle>Review & Submit</CardTitle>
              <CardDescription>
                Please review all information before final submission
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-accent/10 border border-accent/20 rounded-sm p-4">
                <h3 className="font-semibold mb-2">Submission Confirmation</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  By submitting this {dealType === 'infrastructure' ? 'infrastructure project' : 'mineral listing'}, you confirm that:
                </p>
                <ul className="text-sm space-y-2 ml-4">
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-accent mr-2 mt-0.5" />
                    <span>All information provided is accurate and up-to-date</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-accent mr-2 mt-0.5" />
                    <span>You have authority to submit this opportunity on behalf of the sponsor/seller</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-accent mr-2 mt-0.5" />
                    <span>Document availability checklist accurately reflects current status</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle size={16} className="text-accent mr-2 mt-0.5" />
                    <span>You understand this will be reviewed by INMX team before publication</span>
                  </li>
                </ul>
              </div>

              <Separator />

              <div>
                <h3 className="font-semibold mb-3">Summary</h3>
                <div className="space-y-2 text-sm">
                  {dealType === 'infrastructure' ? (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Project:</span>
                        <span className="font-medium">{infraDeal.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Sector:</span>
                        <span className="font-medium">{infraDeal.sector}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Capital Required:</span>
                        <span className="font-medium font-mono">${parseFloat(infraDeal.capital_required || 0).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Target IRR:</span>
                        <span className="font-medium">{infraDeal.target_irr}%</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Commodity:</span>
                        <span className="font-medium">{mineralDeal.commodity}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Grade:</span>
                        <span className="font-medium">{mineralDeal.grade}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Volume:</span>
                        <span className="font-medium font-mono">{parseFloat(mineralDeal.volume || 0).toLocaleString()} {mineralDeal.volume_unit}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Contract:</span>
                        <span className="font-medium">{mineralDeal.contract_duration}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Documents Available:</span>
                    <span className="font-medium">{availableDocs.length}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => {
              if (step === 2 && initialType) {
                navigate(initialType === 'infrastructure' ? '/deals' : '/minerals');
              } else {
                setStep(s => s - 1);
              }
            }}
            disabled={step === 1}
            data-testid="prev-step-button"
          >
            <ChevronLeft size={16} className="mr-2" />
            {step === 2 && initialType ? 'Back to Listing' : 'Previous'}
          </Button>

          <div className="flex items-center space-x-3">
            {step < totalSteps && (
              <Button
                onClick={() => {
                  if (validateStep()) setStep(s => s + 1);
                }}
                data-testid="next-step-button"
              >
                Next
                <ChevronRight size={16} className="ml-2" />
              </Button>
            )}
            {step === totalSteps && (
              <Button
                onClick={handleSubmit}
                disabled={loading || availableDocs.length === 0}
                className="min-w-[150px]"
                data-testid="submit-button"
              >
                {loading ? 'Submitting...' : 'Submit Deal'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
